import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

// Fatia A — homologação do importador: registros pendentes de revisão,
// candidatos a duplicado (decisão humana), pacotes recebidos e categorias
// canônicas. Aprovar um registro PUBLICA o estabelecimento (is_approved=true)
// — a publicação é decisão exclusiva do admin, nunca automática.
const TABS = [
  { id: 'upload', label: 'Upload' },
  { id: 'records', label: 'Registros' },
  { id: 'duplicates', label: 'Duplicidades' },
  { id: 'packages', label: 'Pacotes' },
  { id: 'categories', label: 'Categorias' },
];

const PACKAGE_STATUS_META = {
  RECEIVED: { label: 'Recebido', color: '#1d4ed8', bg: '#eff6ff' },
  VALIDATING: { label: 'Validando', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  PROCESSING: { label: 'Processando', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  COMPLETED: { label: 'Concluído', color: 'var(--nc-success-text)', bg: 'var(--nc-success-soft)' },
  PARTIAL: { label: 'Parcial', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  REJECTED: { label: 'Rejeitado', color: 'var(--nc-danger-text)', bg: 'var(--nc-danger-soft)' },
  QUARANTINED: { label: 'Quarentena', color: 'var(--nc-boost)', bg: '#f5f3ff' },
};

// outbox de uploads HTTP (go-live sem SFTP) — LE-042 + descarte (DISCARDED)
const UPLOAD_STATUS_META = {
  PENDING: { label: 'Aguardando worker', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  DELIVERING: { label: 'Entregando', color: '#1d4ed8', bg: '#eff6ff' },
  DELIVERED: { label: 'Entregue', color: 'var(--nc-success-text)', bg: 'var(--nc-success-soft)' },
  DISCARDED: { label: 'Descartado', color: 'var(--nc-text-secondary)', bg: 'var(--nc-surface-alt)' },
};

const RECORD_STATUS_META = {
  PENDING_REVIEW: { label: 'Aguardando revisão', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  APPROVED: { label: 'Aprovado', color: 'var(--nc-success-text)', bg: 'var(--nc-success-soft)' },
  REJECTED: { label: 'Rejeitado', color: 'var(--nc-danger-text)', bg: 'var(--nc-danger-soft)' },
  MERGED: { label: 'Mesclado', color: '#1d4ed8', bg: '#eff6ff' },
  REVIEW: { label: 'Em revisão', color: 'var(--nc-boost)', bg: '#f5f3ff' },
};

// Lista permitida de icon_key (espelho de services/importer/categories.js).
const ALLOWED_ICON_KEYS = [
  'utensils', 'martini-glass', 'shopping-cart', 'cross', 'stethoscope',
  'croissant', 'shirt', 'wrench', 'fuel', 'bed', 'dumbbell', 'scissors',
  'graduation-cap', 'paw-print', 'tractor', 'store',
];

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function Badge({ meta }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 999,
      color: meta.color, background: meta.bg, whiteSpace: 'nowrap',
    }}>
      {meta.label}
    </span>
  );
}

export function AdminImportsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('records');

  const [records, setRecords] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // upload HTTP de ZIP (go-live sem SFTP)
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCity, setUploadCity] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [uploadFilter, setUploadFilter] = useState('');
  const [discardingId, setDiscardingId] = useState(null);

  // slug da cidade → id (para pacotes multi-cidade na homologação)
  const [cityMap, setCityMap] = useState({});
  const [citiesList, setCitiesList] = useState([]);
  // cidade selecionada por registro/candidato (key = id do registro)
  const [selectedCities, setSelectedCities] = useState({});

  const showMsg = (type, text) => setMessage({ type, text });

  useEffect(() => {
    api.get('/admin/cities')
      .then(({ data }) => {
        const map = {};
        (data.data || []).forEach((c) => { if (c.slug) map[c.slug] = c.id; });
        setCityMap(map);
        setCitiesList(data.data || []);
      })
      .catch(() => { /* cidades indisponíveis — slug não mapeado */ });
  }, []);

  const fetchTab = useCallback(async (which) => {
    setLoading(true);
    setMessage(null);
    try {
      if (which === 'records') {
        const { data } = await api.get('/admin/imports/records', { params: { status: 'PENDING_REVIEW', limit: 100 } });
        setRecords(data.data || []);
      } else if (which === 'uploads') {
        const { data } = await api.get('/admin/imports/uploads', {
          params: { ...(uploadFilter ? { status: uploadFilter } : {}), limit: 50 },
        });
        setUploads(data.data || []);
      } else if (which === 'duplicates') {
        const { data } = await api.get('/admin/imports/duplicates', { params: { limit: 100 } });
        setDuplicates(data.data || []);
      } else if (which === 'packages') {
        const { data } = await api.get('/admin/imports/packages', { params: { limit: 50 } });
        setPackages(data.data || []);
      } else if (which === 'categories') {
        const { data } = await api.get('/admin/imports/categories');
        setCategories(data.data || []);
      }
    } catch (err) {
      showMsg('err', err.response?.data?.error?.message || 'Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  }, [uploadFilter]);

  useEffect(() => { fetchTab(tab); }, [tab, fetchTab]);

  // ── upload HTTP de ZIP ─────────────────────────────────
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) { setUploadError('Selecione um arquivo .zip'); return; }
    setUploading(true);
    setUploadResult(null);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append('package', uploadFile);
      if (uploadCity) form.append('city_slug', uploadCity);
      const { data } = await api.post('/admin/imports/upload', form);
      setUploadResult({
        package_name: data.package_name,
        zip_sha256: data.zip_sha256,
        zip_bytes: data.zip_bytes,
      });
      setUploadFile(null);
      fetchTab('uploads'); // recarrega a outbox (o upload entra como PENDING)
    } catch (err) {
      setUploadError(err.response?.data?.error?.message || 'Erro ao enviar o pacote.');
    } finally {
      setUploading(false);
    }
  };

  // ── descarte de upload preso (DISCARDED) ────────────────
  const discardUpload = async (up) => {
    if (!window.confirm(`Descartar "${up.package_name}"? O pacote NÃO será entregue ao worker (ainda ${up.status === 'PENDING' ? 'aguardando' : 'sendo entregue'}).`)) return;
    setDiscardingId(up.id);
    try {
      await api.post(`/admin/imports/uploads/${up.id}/discard`);
      showMsg('ok', 'Upload descartado — o worker não vai entregar este pacote.');
      fetchTab('uploads');
    } catch (err) {
      showMsg('err', err.response?.data?.error?.message || 'Erro ao descartar upload.');
    } finally {
      setDiscardingId(null);
    }
  };

  // ── ações ──────────────────────────────────────────────
  const approveRecord = async (record) => {
    const cityId = selectedCities[record.id];
    try {
      const { data } = await api.post(`/admin/imports/records/${record.id}/approve`, cityId ? { city_id: cityId } : {});
      showMsg('ok', `"${record.payload?.name}" publicado${data.location_source === 'city_center' ? ' (coordenadas do centro da cidade)' : ''}!`);
      fetchTab('records');
    } catch (err) {
      showMsg('err', err.response?.data?.error?.message || 'Erro ao aprovar registro.');
    }
  };

  const rejectRecord = async (record) => {
    if (!window.confirm(`Rejeitar "${record.payload?.name}"? O registro será descartado.`)) return;
    try {
      await api.post(`/admin/imports/records/${record.id}/reject`);
      showMsg('ok', 'Registro rejeitado.');
      fetchTab('records');
    } catch (err) {
      showMsg('err', err.response?.data?.error?.message || 'Erro ao rejeitar registro.');
    }
  };

  const duplicateDecision = async (cand, decision) => {
    if (decision === 'MESCLAR' && !window.confirm('Mesclar o registro importado com o estabelecimento existente? Contatos ausentes serão preenchidos.')) return;
    if (decision === 'REJEITAR' && !window.confirm('Registrar que NÃO é o mesmo estabelecimento? O registro continua aguardando revisão.')) return;
    const cityId = selectedCities[cand.record_id];
    try {
      const { data } = await api.post(`/admin/imports/duplicates/${cand.id}/decision`, {
        decision,
        ...(cityId ? { city_id: cityId } : {}),
      });
      const labels = { MESCLAR: 'mesclado ao estabelecimento existente', CRIAR_NOVO: 'novo estabelecimento criado', REJEITAR: 'candidato rejeitado' };
      showMsg('ok', `Decisão registrada: ${labels[data.action]}.`);
      fetchTab('duplicates');
    } catch (err) {
      showMsg('err', err.response?.data?.error?.message || 'Erro ao registrar decisão.');
    }
  };

  // ── categorias ─────────────────────────────────────────
  const [catForm, setCatForm] = useState({ slug: '', name: '', icon_key: 'store', color_key: '', aliases: '' });
  const [catSaving, setCatSaving] = useState(false);

  const createCategory = async (e) => {
    e.preventDefault();
    setCatSaving(true);
    try {
      await api.post('/admin/imports/categories', {
        slug: catForm.slug.trim().toLowerCase(),
        name: catForm.name.trim(),
        icon_key: catForm.icon_key,
        color_key: catForm.color_key.trim() || null,
        aliases: catForm.aliases.split(/[,;]/).map((a) => a.trim()).filter(Boolean),
      });
      showMsg('ok', 'Categoria criada.');
      setCatForm({ slug: '', name: '', icon_key: 'store', color_key: '', aliases: '' });
      fetchTab('categories');
    } catch (err) {
      showMsg('err', err.response?.data?.error?.message || 'Erro ao criar categoria.');
    } finally {
      setCatSaving(false);
    }
  };

  const toggleCategory = async (cat) => {
    try {
      await api.patch(`/admin/imports/categories/${cat.id}`, { is_active: !cat.is_active });
      fetchTab('categories');
    } catch (err) {
      showMsg('err', err.response?.data?.error?.message || 'Erro ao atualizar categoria.');
    }
  };

  // pacote expandido (registros do pacote)
  const [expandedPackage, setExpandedPackage] = useState(null);
  const [packageRecords, setPackageRecords] = useState([]);
  const [pkgLoading, setPkgLoading] = useState(false);

  const togglePackage = async (pkg) => {
    if (expandedPackage === pkg.id) { setExpandedPackage(null); return; }
    setExpandedPackage(pkg.id);
    setPkgLoading(true);
    try {
      const { data } = await api.get(`/admin/imports/packages/${pkg.id}`, { params: { limit: 50 } });
      setPackageRecords(data.data || []);
    } catch {
      setPackageRecords([]);
    } finally {
      setPkgLoading(false);
    }
  };

  const needsCity = (item) => {
    const cities = item.package_cities;
    return Array.isArray(cities) && cities.length > 1;
  };

  const CitySelect = ({ item }) => {
    if (!needsCity(item)) return null;
    const cities = item.package_cities || [];
    return (
      <select
        aria-label="Cidade do estabelecimento"
        value={selectedCities[item.record_id || item.id] || ''}
        onChange={(e) => setSelectedCities(prev => ({ ...prev, [item.record_id || item.id]: e.target.value }))}
        style={{
          width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db',
          background: 'var(--nc-surface)', fontSize: 12, marginBottom: 10, color: 'var(--nc-text)',
        }}
      >
        <option value="">Selecione a cidade *</option>
        {cities.map((c) => (
          <option key={c.slug} value={cityMap[c.slug] || ''}>{c.name || c.slug}</option>
        ))}
      </select>
    );
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--nc-border)',
    fontSize: 14, marginBottom: 10, outline: 'none', background: 'var(--nc-surface)', color: 'var(--nc-text)', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--nc-text-secondary)', marginBottom: 4 };

  return (
    <div className="nc-page">
      <div style={{
        background: 'linear-gradient(135deg, var(--nc-primary) 0%, var(--nc-primary-dark) 100%)',
        color: 'var(--nc-on-primary)', padding: '18px 16px 18px', borderRadius: '0 0 22px 22px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <button onClick={() => navigate('/admin')} aria-label="Voltar" className="nc-iconbtn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="nc-page-title">Importações</h1>
            <p style={{ fontSize: 11, opacity: 0.8, margin: '2px 0 0' }}>Homologação do coletor • publicação só com sua aprovação</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                background: tab === t.id ? '#fff' : 'rgba(255,255,255,0.15)',
                color: tab === t.id ? '#0d5c3b' : '#fff', fontSize: 12, fontWeight: 500
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {message && (
          <div role="status" style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13,
            background: message.type === 'ok' ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${message.type === 'ok' ? '#bbf7d0' : '#fecaca'}`,
            color: message.type === 'ok' ? '#15803d' : '#b91c1c'
          }}>
            {message.text}
          </div>
        )}

        {loading ? <Loading /> : (
          <>
            {/* ───────── Upload de ZIP ───────── */}
            {tab === 'upload' && (
              <>
                <form onSubmit={handleUpload} style={{ background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)', padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 4 }}>Enviar pacote do coletor (ZIP)</div>
                  <p className="nc-hint" style={{ margin: '0 0 14px' }}>
                    Pré-go-live sem SFTP: envie o ZIP pela tela. O worker entrega em <code style={{ fontSize: 12 }}>incoming/</code> e o processa como <strong>PENDING_REVIEW</strong> (nada é publicado sem a sua aprovação na aba Registros).
                  </p>

                  <label style={labelStyle}>Cidade (opcional — marca o pacote)</label>
                  <select
                    value={uploadCity}
                    onChange={(e) => setUploadCity(e.target.value)}
                    style={{ ...inputStyle, background: 'var(--nc-surface)' }}
                    aria-label="Cidade"
                  >
                    <option value="">Sem cidade definida</option>
                    {citiesList.map((c) => (
                      <option key={c.id} value={c.slug}>{c.name} ({c.slug})</option>
                    ))}
                  </select>

                  <label style={labelStyle}>Arquivo .zip *</label>
                  <input
                    type="file"
                    accept=".zip,application/zip,application/x-zip-compressed,application/octet-stream"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    style={{ ...inputStyle, padding: '8px', background: 'var(--nc-surface)' }}
                    aria-label="Arquivo ZIP"
                  />

                  {uploadError && <div role="alert" style={{ fontSize: 12, color: 'var(--nc-danger-text)', marginBottom: 8 }}>{uploadError}</div>}

                  <button type="submit" disabled={uploading || !uploadFile} className="nc-btn-primary" style={{ width: '100%', opacity: uploading || !uploadFile ? 0.6 : 1 }}>
                    {uploading ? 'Enviando...' : 'Enviar pacote'}
                  </button>
                </form>

                {uploadResult && (
                  <div style={{ background: 'var(--nc-success-soft)', border: '1px solid var(--nc-success-border)', borderRadius: 14, padding: 14, marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#065f46', marginBottom: 6 }}>✓ Pacote aceito e aguardando o worker</div>
                    <div style={{ fontSize: 12, color: 'var(--nc-success-text)', marginBottom: 2 }}>nome: <code style={{ fontSize: 11 }}>{uploadResult.package_name}</code></div>
                    <div style={{ fontSize: 12, color: 'var(--nc-success-text)' }}>
                      tamanho: {(uploadResult.zip_bytes / 1024).toFixed(0)} KB • sha256: <code style={{ fontSize: 11 }}>{uploadResult.zip_sha256.slice(0, 16)}…</code>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--nc-success-text)', marginTop: 6 }}>
                      O processamento acontece em até alguns segundos — acompanhe o status aqui nesta lista (Pacotes mostra apenas o que o worker já processou). Um pacote rejeitado pelo worker aparece ali com o motivo.
                    </div>
                  </div>
                )}

                {/* ── outbox de uploads ── */}
                <div style={{ background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)', padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>Pacotes enviados por upload</div>
                    <select
                      value={uploadFilter}
                      onChange={(e) => setUploadFilter(e.target.value)}
                      aria-label="Filtrar uploads por status"
                      style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--nc-border)', background: 'var(--nc-surface)', fontSize: 12, color: 'var(--nc-text)' }}
                    >
                      <option value="">Todos</option>
                      <option value="PENDING">Aguardando worker</option>
                      <option value="DELIVERING">Entregando</option>
                      <option value="DELIVERED">Entregues</option>
                      <option value="DISCARDED">Descartados</option>
                    </select>
                  </div>

                  {uploads.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', textAlign: 'center', padding: '16px 0' }}>
                      Nenhum upload{uploadFilter ? ` com status ${UPLOAD_STATUS_META[uploadFilter]?.label} ` : ' '}ainda.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {uploads.map((up) => {
                        const meta = UPLOAD_STATUS_META[up.status] || { label: up.status, color: 'var(--nc-text-secondary)', bg: 'var(--nc-surface-alt)' };
                        const cancellable = up.status === 'PENDING' || up.status === 'DELIVERING';
                        return (
                          <div key={up.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--nc-border)', background: '#f9fafb' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nc-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {up.package_name}
                              </div>
                              <div className="nc-caption" style={{ marginTop: 2 }}>
                                {formatDate(up.created_at)} • {(up.zip_bytes / 1024).toFixed(0)} KB
                                {up.city_slug ? ` • ${up.city_slug}` : ''}
                                {up.uploaded_by_name ? ` • ${up.uploaded_by_name}` : ''}
                              </div>
                              {up.error && (
                                <div style={{ fontSize: 11, color: 'var(--nc-danger-text)', marginTop: 4 }}>⚠ {up.error}</div>
                              )}
                            </div>
                            <Badge meta={meta} />
                            {cancellable && (
                              <button
                                onClick={() => discardUpload(up)}
                                disabled={discardingId === up.id}
                                style={{
                                  padding: '6px 12px', borderRadius: 8, border: '1px solid var(--nc-danger-border)',
                                  background: 'var(--nc-danger-soft)', color: 'var(--nc-danger-text)', fontSize: 11, fontWeight: 600,
                                  cursor: discardingId === up.id ? 'wait' : 'pointer', whiteSpace: 'nowrap'
                                }}
                              >
                                {discardingId === up.id ? 'Descartando…' : 'Descartar'}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* ───────── Registros pendentes ───────── */}
            {tab === 'records' && (
              records.length === 0 ? (
                <EmptyState icon="📥" title="Nenhum registro aguardando" description="Pacotes importados entram aqui como PENDING_REVIEW até você homologar." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {records.map(r => (
                    <div key={r.id} style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{
                          width: 44, height: 44, borderRadius: 10, background: 'var(--nc-success-soft)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0
                        }}>🏪</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>{r.payload?.name || 'Sem nome'}</div>
                          <div className="nc-hint" style={{ marginTop: 2 }}>
                            {r.category_name || r.category_slug || 'Sem categoria'}{r.payload?.address ? ` • ${r.payload.address}` : ''}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', marginTop: 2 }}>
                            {(r.payload?.phone || r.payload?.whatsapp) ? `📞 ${r.payload.phone || r.payload.whatsapp} • ` : ''}pacote {r.export_id || `#${r.package_id}`}
                          </div>
                          {Array.isArray(r.reasons) && r.reasons.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                              {r.reasons.map((reason, i) => (
                                <span key={i} style={{
                                  fontSize: 10, background: 'var(--nc-warning-soft)', color: '#92400e',
                                  border: '1px solid var(--nc-warning-border)', padding: '2px 8px', borderRadius: 999
                                }}>
                                  {reason}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div style={{ marginTop: 10 }}>
                        <CitySelect item={r} />
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => approveRecord(r)}
                          disabled={needsCity(r) && !selectedCities[r.id]}
                          className="nc-btn-primary"
                          style={{
                            flex: 1, padding: '9px 0', fontSize: 13,
                            opacity: needsCity(r) && !selectedCities[r.id] ? 0.5 : 1,
                            cursor: needsCity(r) && !selectedCities[r.id] ? 'not-allowed' : 'pointer',
                          }}
                        >
                          ✓ Aprovar e publicar
                        </button>
                        <button
                          onClick={() => rejectRecord(r)}
                          style={{
                            flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid var(--nc-danger-border)',
                            background: 'var(--nc-danger-soft)', color: 'var(--nc-danger-text)', fontSize: 13, fontWeight: 500, cursor: 'pointer'
                          }}
                        >
                          Rejeitar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}

            {/* ───────── Duplicidades ───────── */}
            {tab === 'duplicates' && (
              duplicates.length === 0 ? (
                <EmptyState icon="🔍" title="Nenhuma duplicidade em aberto" description="Possíveis duplicados do coletor aparecem aqui para decisão humana (nunca são mesclados sozinhos)." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {duplicates.map(d => {
                    const signals = d.signals || {};
                    return (
                      <div key={d.id} style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-warning-border)', padding: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
                            ⚠️ {Math.round(d.similarity * 100)}% de semelhança
                          </span>
                          {d.distance_m != null && (
                            <span className="nc-caption">distância: {d.distance_m} m</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                          <div style={{ flex: 1, background: 'var(--nc-warning-soft)', borderRadius: 10, padding: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Importado</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text)' }}>{d.payload?.name || 'Sem nome'}</div>
                            <div style={{ fontSize: 11, color: 'var(--nc-text-secondary)' }}>{d.payload?.address}</div>
                            {d.payload?.phone && <div className="nc-caption">📞 {d.payload.phone}</div>}
                          </div>
                          <div style={{ flex: 1, background: 'var(--nc-surface-alt)', borderRadius: 10, padding: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--nc-text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Existente</div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text)' }}>{d.business_name}</div>
                            <div style={{ fontSize: 11, color: 'var(--nc-text-secondary)' }}>{d.business_address}</div>
                            {(d.business_phone || d.business_whatsapp) && <div className="nc-caption">📞 {d.business_phone || d.business_whatsapp}</div>}
                          </div>
                        </div>
                        {(signals.name_similarity != null || signals.phone_equal) && (
                          <div style={{ fontSize: 11, color: 'var(--nc-text-secondary)', marginTop: 8 }}>
                            {signals.name_similarity != null && `nome ${(signals.name_similarity * 100).toFixed(0)}% • `}
                            {signals.address_similarity != null && `endereço ${(signals.address_similarity * 100).toFixed(0)}% • `}
                            {signals.phone_equal && 'telefone igual • '}
                            {signals.cep_equal && 'CEP igual • '}
                            {signals.coordinates_near && 'coordenadas próximas'}
                          </div>
                        )}
                        <div style={{ marginTop: 10 }}>
                          <CitySelect item={d} />
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            onClick={() => duplicateDecision(d, 'MESCLAR')}
                            style={{
                              flex: 1, padding: '9px 0', borderRadius: 8, border: 'none',
                              background: 'var(--nc-primary)', color: 'var(--nc-on-primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                            }}
                          >
                            Mesclar
                          </button>
                          <button
                            onClick={() => duplicateDecision(d, 'CRIAR_NOVO')}
                            disabled={needsCity(d) && !selectedCities[d.record_id]}
                            style={{
                              flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid var(--nc-border)',
                              background: 'var(--nc-surface)', color: '#1d4ed8', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              opacity: needsCity(d) && !selectedCities[d.record_id] ? 0.5 : 1,
                            }}
                          >
                            Criar novo
                          </button>
                          <button
                            onClick={() => duplicateDecision(d, 'REJEITAR')}
                            style={{
                              flex: 1, padding: '9px 0', borderRadius: 8, border: '1px solid var(--nc-danger-border)',
                              background: 'var(--nc-danger-soft)', color: 'var(--nc-danger-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                            }}
                          >
                            Rejeitar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {/* ───────── Pacotes ───────── */}
            {tab === 'packages' && (
              packages.length === 0 ? (
                <EmptyState icon="📦" title="Nenhum pacote" description="ZIPs entregues pelo coletor aparecem aqui com o estado da esteira." />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {packages.map(p => {
                    const meta = PACKAGE_STATUS_META[p.status] || { label: p.status, color: 'var(--nc-text-secondary)', bg: 'var(--nc-surface-alt)' };
                    return (
                      <div key={p.id} style={{ background: 'var(--nc-surface)', borderRadius: 12, border: '1px solid var(--nc-border)', padding: 14 }}>
                        <div className="nc-row">
                          <div style={{
                            width: 40, height: 40, borderRadius: 10, background: 'var(--nc-surface-alt)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0
                          }}>📦</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--nc-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.export_id || `#${p.id}`}
                            </div>
                            <div className="nc-caption">
                              {formatDate(p.created_at)} • {p.record_count ?? 0} registros • {p.pending_count ?? 0} aguardando
                            </div>
                          </div>
                          <Badge meta={meta} />
                        </div>
                        {p.error && (
                          <div style={{ fontSize: 11, color: 'var(--nc-danger-text)', background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)', borderRadius: 8, padding: '6px 10px', marginTop: 8 }}>
                            {p.error}
                          </div>
                        )}
                        <button
                          onClick={() => togglePackage(p)}
                          style={{
                            width: '100%', marginTop: 10, padding: '8px 0', borderRadius: 8,
                            border: '1px solid var(--nc-border)', background: '#f9fafb',
                            color: 'var(--nc-text-dim)', fontSize: 12, fontWeight: 500, cursor: 'pointer'
                          }}
                        >
                          {expandedPackage === p.id ? 'Ocultar registros' : 'Ver registros'}
                        </button>
                        {expandedPackage === p.id && (
                          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {pkgLoading ? <Loading /> : packageRecords.length === 0 ? (
                              <div style={{ fontSize: 12, color: 'var(--nc-text-muted)', textAlign: 'center', padding: 10 }}>Sem registros.</div>
                            ) : packageRecords.map(r => {
                              const rm = RECORD_STATUS_META[r.status] || { label: r.status, color: 'var(--nc-text-secondary)', bg: 'var(--nc-surface-alt)' };
                              return (
                                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f9fafb', borderRadius: 8, padding: '8px 10px' }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nc-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {r.payload?.name || 'Sem nome'}
                                    </div>
                                    {r.business_name && <div style={{ fontSize: 11, color: 'var(--nc-success-text)' }}>→ {r.business_name}</div>}
                                  </div>
                                  <Badge meta={rm} />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {/* ───────── Categorias ───────── */}
            {tab === 'categories' && (
              <>
                <form onSubmit={createCategory} style={{ background: 'var(--nc-surface)', borderRadius: 14, border: '1px solid var(--nc-border)', padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)', marginBottom: 10 }}>Nova categoria canônica</div>
                  <label style={labelStyle}>Slug *</label>
                  <input value={catForm.slug} onChange={(e) => setCatForm({ ...catForm, slug: e.target.value })} required placeholder="ex.: pet-shop" style={inputStyle} aria-label="Slug" />
                  <label style={labelStyle}>Nome *</label>
                  <input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} required placeholder="ex.: Pet shop" style={inputStyle} aria-label="Nome" />
                  <label style={labelStyle}>Ícone (lista permitida) *</label>
                  <select value={catForm.icon_key} onChange={(e) => setCatForm({ ...catForm, icon_key: e.target.value })} style={{ ...inputStyle, background: 'var(--nc-surface)' }} aria-label="Ícone">
                    {ALLOWED_ICON_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <label style={labelStyle}>Cor (opcional)</label>
                  <input value={catForm.color_key} onChange={(e) => setCatForm({ ...catForm, color_key: e.target.value })} placeholder="ex.: teal" style={inputStyle} aria-label="Cor" />
                  <label style={labelStyle}>Aliases do coletor (separados por vírgula)</label>
                  <input value={catForm.aliases} onChange={(e) => setCatForm({ ...catForm, aliases: e.target.value })} placeholder="ex.: pet, petshop, animal" style={inputStyle} aria-label="Aliases" />
                  <button type="submit" disabled={catSaving} className="nc-btn-primary" style={{ width: '100%' }}>
                    {catSaving ? 'Criando...' : 'Criar categoria'}
                  </button>
                </form>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {categories.length === 0 ? (
                    <EmptyState icon="🏷️" title="Nenhuma categoria" description="Crie a primeira categoria canônica acima." />
                  ) : categories.map(cat => (
                    <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)' }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 10, background: cat.is_active ? '#f0fdf4' : '#f3f4f6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
                      }}>🏷️</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nc-text)' }}>
                          {cat.name}
                          {!cat.is_active && <span style={{ fontSize: 11, color: 'var(--nc-danger-text)', marginLeft: 6 }}>(inativa)</span>}
                        </div>
                        <div className="nc-caption">
                          <code style={{ fontSize: 11 }}>{cat.slug}</code> • {cat.icon_key}{cat.record_count ? ` • ${cat.record_count} registros` : ''}
                        </div>
                        {Array.isArray(cat.aliases) && cat.aliases.length > 0 && (
                          <div className="nc-caption" style={{ marginTop: 2 }}>aliases: {cat.aliases.join(', ')}</div>
                        )}
                      </div>
                      <button
                        onClick={() => toggleCategory(cat)}
                        style={{
                          padding: '7px 12px', borderRadius: 8, border: '1px solid var(--nc-border)',
                          background: 'var(--nc-surface)', color: cat.is_active ? '#b45309' : '#15803d',
                          fontSize: 12, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap'
                        }}
                      >
                        {cat.is_active ? 'Desativar' : 'Ativar'}
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
